import { useState, useCallback, useRef, useEffect } from 'react';
import { query } from '../graphql/client';
import { IS_USERNAME_AVAILABLE } from '../graphql/operations';

const USERNAME_REGEX = /^[a-z0-9][a-z0-9-]{1,28}[a-z0-9]$/;

export type UsernameValidationStatus = 'idle' | 'checking' | 'available' | 'taken' | 'invalid';

interface UseUsernameSetupResult {
  username: string;
  setUsername: (value: string) => void;
  status: UsernameValidationStatus;
  error: string | null;
  isValid: boolean;
}

/**
 * Formats input to kebab-case for usernames
 */
export function toKebabCase(input: string): string {
  return input
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove accents
    .replace(/[^a-z0-9]+/g, '-')     // Replace non-alphanumeric with hyphens
    .replace(/-+/g, '-')              // Collapse multiple hyphens
    .replace(/^-|-$/g, '')            // Trim leading/trailing hyphens
    .slice(0, 30);                    // Max 30 chars
}

/**
 * Validates username format
 */
function validateUsername(username: string): { valid: boolean; error: string | null } {
  if (!username) {
    return { valid: false, error: null };
  }

  if (username.length < 3) {
    return { valid: false, error: 'Username must be at least 3 characters' };
  }

  if (username.length > 30) {
    return { valid: false, error: 'Username must be at most 30 characters' };
  }

  if (!USERNAME_REGEX.test(username)) {
    return { valid: false, error: 'Username can only contain lowercase letters, numbers, and hyphens (not at start/end)' };
  }

  return { valid: true, error: null };
}

export function useUsernameSetup(initialUsername?: string, skipUsername?: string): UseUsernameSetupResult {
  const [username, setUsernameState] = useState(initialUsername ? toKebabCase(initialUsername) : '');
  const [status, setStatus] = useState<UsernameValidationStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastCheckedRef = useRef<string>('');

  const checkAvailability = useCallback(async (name: string) => {
    if (lastCheckedRef.current === name) return;
    lastCheckedRef.current = name;

    // Skip check if it's the user's current username
    if (skipUsername && name === skipUsername) {
      setStatus('available');
      setError(null);
      return;
    }

    setStatus('checking');
    try {
      const data = await query<{ isUsernameAvailable: boolean }>(IS_USERNAME_AVAILABLE, { username: name });
      if (data.isUsernameAvailable) {
        setStatus('available');
        setError(null);
      } else {
        setStatus('taken');
        setError('This username is already taken');
      }
    } catch (err) {
      console.error('Failed to check username availability:', err);
      setStatus('idle');
      setError('Failed to check availability');
    }
  }, [skipUsername]);

  const setUsername = useCallback((value: string) => {
    // Auto-format to kebab-case
    const formatted = toKebabCase(value);
    setUsernameState(formatted);

    // Clear any pending debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Validate format first
    const validation = validateUsername(formatted);
    if (!validation.valid) {
      setStatus('invalid');
      setError(validation.error);
      return;
    }

    // Debounce availability check
    setStatus('checking');
    debounceRef.current = setTimeout(() => {
      checkAvailability(formatted);
    }, 300);
  }, [checkAvailability]);

  // Perform initial validation if initialUsername is provided
  useEffect(() => {
    if (initialUsername) {
      const formatted = toKebabCase(initialUsername);
      const validation = validateUsername(formatted);
      if (validation.valid) {
        checkAvailability(formatted);
      } else {
        setStatus('invalid');
        setError(validation.error);
      }
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const isValid = status === 'available';

  return {
    username,
    setUsername,
    status,
    error,
    isValid,
  };
}
